<template>
  <div class="subscription-plans-view">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>
    
    <div class="content-wrapper">
      <div class="header">
        <div class="header-content">
          <h1 class="page-title">Choose Your Plan</h1>
          <p class="page-subtitle">Select the perfect plan for your website monitoring needs</p>
        </div>
      </div>

      <!-- Current Subscription Info -->
      <div v-if="userData?.subscription && !loading" class="current-subscription-info">
        <div class="subscription-info-header">
          <h2 class="subscription-info-title">Current Subscription</h2>
        </div>
        <div class="subscription-info-content">
          <div class="info-row">
            <span class="info-label">Plan:</span>
            <span class="info-value">{{ getCurrentPlanName() }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value status-badge" :class="userData.subscription.status">
              {{ userData.subscription.status }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">Sites:</span>
            <span class="info-value">{{ userData.subscription.siteCount || 'N/A' }}</span>
          </div>
          <div class="info-row" v-if="userData.subscription.nextBillingDate">
            <span class="info-label">Next Billing Date:</span>
            <span class="info-value">{{ formatNextBillingDate() }}</span>
          </div>
        </div>
      </div>

      <!-- Scheduled Cancellation Notification -->
      <div v-if="hasScheduledCancellation" class="cancellation-notification">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <div class="notification-content">
          <strong>Subscription scheduled to cancel</strong>
          <span>Your subscription will be cancelled on {{ formatCancellationDate() }}. <a href="#" @click.prevent="manageSubscription" class="notification-link">Don't cancel</a></span>
        </div>
      </div>

      <!-- Scheduled Downgrade Notification -->
      <div v-if="hasScheduledDowngrade" class="downgrade-notification">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <polyline points="19 12 12 19 5 12"></polyline>
        </svg>
        <div class="notification-content">
          <strong>Downgrade scheduled</strong>
          <span>Your subscription will be downgraded<template v-if="scheduledDowngradePlanName"> to <strong>{{ scheduledDowngradePlanName }}</strong></template> on {{ formatScheduledChangeDate() }}. <a href="#" @click.prevent="manageSubscription" class="notification-link">Manage subscription</a></span>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading subscription plans...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="fetchData">Try Again</button>
      </div>
      
      <div v-else-if="(!monthlyPlans || monthlyPlans.length === 0) && (!yearlyPlans || yearlyPlans.length === 0)" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        </svg>
        <p>No subscription plans available.</p>
      </div>
      
      <div v-else class="plans-container">
        <!-- Monthly Plans Section -->
        <div v-if="monthlyPlans && monthlyPlans.length > 0" class="plans-section">
          <h2 class="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            Monthly Plans
          </h2>
          <div class="plans-grid">
            <div
              v-for="plan in monthlyPlans"
              :key="plan.id"
              class="plan-card"
              :class="{ 
                'has-discount': plan.discount && plan.discount.isActive, 
                'free-plan': plan.isFree,
                'popular': isMostPopular(plan),
                'current': isCurrentPlan(plan)
              }"
              @click="selectPlan(plan)"
            >
          <!-- Popular badge -->
          <div v-if="isMostPopular(plan)" class="popular-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
            Most Popular
          </div>
          
          <!-- Discount badge -->
          <div v-if="plan.discount && plan.discount.isActive" class="discount-badge">
            {{ formatDiscount(plan.discount) }}
          </div>
          
          <!-- Current plan indicator -->
          <div v-if="isCurrentPlan(plan)" class="current-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            Current Plan
          </div>
          
          <div class="plan-header">
            <h3 class="plan-name">{{ getPlanName(plan) }}</h3>
            <div v-if="plan.isFree" class="free-badge">FREE</div>
          </div>
          
          <div class="plan-price">
            <span class="price-currency">€</span>
            <span class="price-amount">{{ Math.floor(plan.finalPrice) }}</span>
            <span class="price-cents">.{{ (plan.finalPrice % 1).toFixed(2).substring(2) }}</span>
            <div class="price-meta">
              <span v-if="plan.basePrice !== plan.finalPrice" class="original-price">
                €{{ plan.basePrice.toFixed(2) }}
              </span>
              <span class="price-period">/ {{ plan.billingCycle }}</span>
            </div>
          </div>
          
          <div class="plan-divider"></div>
          
          <div class="plan-features">
            <div v-if="plan.siteCount !== null" class="feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              <span class="feature-label">Sites</span>
              <span class="feature-value">{{ plan.siteCount }}</span>
            </div>
            <div v-if="plan.description" class="feature description">
              <p class="description-text">{{ plan.description }}</p>
            </div>
          </div>
          
          <div class="plan-footer">
            <div class="plan-footer-buttons">
              <button 
                v-if="!isCurrentPlan(plan)"
                class="select-button" 
                :class="{ 
                  'upgrading': upgradingPlanId === plan.id
                }"
                :disabled="!plan.isActive || plan.isFree || upgradingPlanId !== null || isPlanDisabled(plan)"
                :title="isDowngrade(plan) ? 'Cancel your subscription in order to change to a smaller plan' : ''"
              >
                <span v-if="upgradingPlanId === plan.id" class="btn-content">
                  <span class="btn-spinner"></span>
                  Processing...
                </span>
                <span v-else-if="plan.isFree" class="btn-content">
                  Free Plan
                </span>
                <span v-else-if="plan.isActive && isUpgrade(plan)" class="btn-content">
                  Upgrade
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <polyline points="5 12 12 5 19 12"></polyline>
                  </svg>
                </span>
                <span v-else-if="plan.isActive && isDowngrade(plan)" class="btn-content">
                  Downgrade
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                </span>
                <span v-else-if="plan.isActive" class="btn-content">
                  Select Plan
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </span>
                <span v-else class="btn-content">Unavailable</span>
              </button>
              <button 
                v-if="isCurrentPlan(plan) && !plan.isFree"
                class="manage-subscription-button"
                :disabled="managingSubscription"
                @click.stop="manageSubscription"
              >
                <span class="btn-content">
                  <span v-if="managingSubscription" class="btn-spinner"></span>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  {{ managingSubscription ? 'Loading...' : (hasScheduledCancellation ? 'Re-enable subscription' : 'Manage subscription') }}
                </span>
              </button>
            </div>
          </div>
        </div>
          </div>
        </div>

        <!-- Yearly Plans Section -->
        <div v-if="yearlyPlans && yearlyPlans.length > 0" class="plans-section">
          <h2 class="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Yearly Plans
            <span class="save-badge">Save up to 30%</span>
          </h2>
          <div class="plans-grid">
            <div
              v-for="plan in yearlyPlans"
              :key="plan.id"
              class="plan-card"
              :class="{ 
                'has-discount': plan.discount && plan.discount.isActive, 
                'free-plan': plan.isFree,
                'popular': isMostPopular(plan),
                'current': isCurrentPlan(plan)
              }"
              @click="selectPlan(plan)"
            >
              <!-- Popular badge -->
              <div v-if="isMostPopular(plan)" class="popular-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                Most Popular
              </div>
              
              <!-- Discount badge -->
              <div v-if="plan.discount && plan.discount.isActive" class="discount-badge">
                {{ formatDiscount(plan.discount) }}
              </div>
              
              <!-- Current plan indicator -->
              <div v-if="isCurrentPlan(plan)" class="current-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Current Plan
              </div>
              
              <div class="plan-header">
                <h3 class="plan-name">{{ getPlanName(plan) }}</h3>
                <div v-if="plan.isFree" class="free-badge">FREE</div>
              </div>
              
              <div class="plan-price">
                <span class="price-currency">€</span>
                <span class="price-amount">{{ Math.floor(plan.finalPrice) }}</span>
                <span class="price-cents">.{{ (plan.finalPrice % 1).toFixed(2).substring(2) }}</span>
                <div class="price-meta">
                  <span v-if="plan.basePrice !== plan.finalPrice" class="original-price">
                    €{{ plan.basePrice.toFixed(2) }}
                  </span>
                  <span class="price-period">/ {{ plan.billingCycle }}</span>
                </div>
              </div>
              
              <div class="plan-divider"></div>
              
              <div class="plan-features">
                <div v-if="plan.siteCount !== null" class="feature">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                  <span class="feature-label">Sites</span>
                  <span class="feature-value">{{ plan.siteCount }}</span>
                </div>
                <div v-if="plan.description" class="feature description">
                  <p class="description-text">{{ plan.description }}</p>
                </div>
              </div>
              
              <div class="plan-footer">
                <div class="plan-footer-buttons">
                  <button 
                    v-if="!isCurrentPlan(plan)"
                    class="select-button" 
                    :class="{ 
                      'upgrading': upgradingPlanId === plan.id
                    }"
                    :disabled="!plan.isActive || plan.isFree || upgradingPlanId !== null || isPlanDisabled(plan)"
                    :title="isDowngrade(plan) ? 'Cancel your subscription in order to change to a smaller plan' : ''"
                  >
                    <span v-if="upgradingPlanId === plan.id" class="btn-content">
                      <span class="btn-spinner"></span>
                      Processing...
                    </span>
                    <span v-else-if="plan.isFree" class="btn-content">
                      Free Plan
                    </span>
                    <span v-else-if="plan.isActive && isUpgrade(plan)" class="btn-content">
                      Upgrade
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5 12 12 5 19 12"></polyline>
                      </svg>
                    </span>
                    <span v-else-if="plan.isActive && isDowngrade(plan)" class="btn-content">
                      Downgrade
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <polyline points="19 12 12 19 5 12"></polyline>
                      </svg>
                    </span>
                    <span v-else-if="plan.isActive" class="btn-content">
                      Select Plan
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </span>
                    <span v-else class="btn-content">Unavailable</span>
                  </button>
                  <button 
                    v-if="isCurrentPlan(plan) && !plan.isFree"
                    class="manage-subscription-button"
                    :disabled="managingSubscription"
                    @click.stop="manageSubscription"
                  >
                    <span class="btn-content">
                      <span v-if="managingSubscription" class="btn-spinner"></span>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      {{ managingSubscription ? 'Loading...' : (hasScheduledCancellation ? 'Re-enable subscription' : 'Manage subscription') }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Billing Info Form Modal -->
    <div v-if="showBillingForm" class="modal-overlay" @click.self="closeBillingForm">
      <div class="modal-content billing-form-modal">
        <button class="modal-close" @click="closeBillingForm" :disabled="billingFormLoading">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div class="modal-header">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
            <line x1="1" y1="10" x2="23" y2="10"></line>
          </svg>
          <h2>Billing Information</h2>
        </div>

        <p class="modal-description">Please provide your billing details to continue with your purchase.</p>

        <form @submit.prevent="submitBillingInfo" class="billing-form">
          <!-- Business Checkbox -->
          <label class="business-toggle">
            <input type="checkbox" v-model="billingInfo.isBusiness" :disabled="billingFormLoading">
            <span class="toggle-switch"></span>
            <span class="toggle-label">This is a business purchase</span>
          </label>

          <!-- Billing Name -->
          <div class="form-group">
            <label class="form-label">
              {{ billingInfo.isBusiness ? 'Contact Name' : 'Full Name' }}
              <span class="required">*</span>
            </label>
            <input 
              type="text" 
              v-model="billingInfo.billingName" 
              class="form-input"
              :placeholder="billingInfo.isBusiness ? 'Contact person name' : 'Your full name'"
              :disabled="billingFormLoading"
            >
          </div>

          <!-- Business Fields -->
          <template v-if="billingInfo.isBusiness">
            <!-- Company Name -->
            <div class="form-group">
              <label class="form-label">
                Company Name
                <span class="required">*</span>
              </label>
              <input 
                type="text" 
                v-model="billingInfo.companyName" 
                class="form-input"
                placeholder="Your company name"
                :disabled="billingFormLoading"
              >
            </div>

            <!-- VAT ID -->
            <div class="form-group">
              <label class="form-label">VAT ID</label>
              <input 
                type="text" 
                v-model="billingInfo.vatId" 
                class="form-input"
                placeholder="Enter your VAT ID"
                :disabled="billingFormLoading"
              >
            </div>

            <!-- Address Line -->
            <div class="form-group">
              <label class="form-label">
                Address
                <span class="required">*</span>
              </label>
              <input 
                type="text" 
                v-model="billingInfo.addressLine" 
                class="form-input"
                placeholder="Street address"
                :disabled="billingFormLoading"
              >
            </div>

            <!-- City and Postal Code -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">
                  City
                  <span class="required">*</span>
                </label>
                <input 
                  type="text" 
                  v-model="billingInfo.city" 
                  class="form-input"
                  placeholder="City"
                  :disabled="billingFormLoading"
                >
              </div>
              <div class="form-group">
                <label class="form-label">
                  Postal Code
                  <span class="required">*</span>
                </label>
                <input 
                  type="text" 
                  v-model="billingInfo.postalCode" 
                  class="form-input"
                  placeholder="Postal code"
                  :disabled="billingFormLoading"
                >
              </div>
            </div>
          </template>

          <!-- Postal Code for non-business -->
          <div v-if="!billingInfo.isBusiness" class="form-group">
            <label class="form-label">
              Postal Code
              <span class="required">*</span>
            </label>
            <input 
              type="text" 
              v-model="billingInfo.postalCode" 
              class="form-input"
              placeholder="Postal code"
              :disabled="billingFormLoading"
            >
          </div>

          <!-- Country -->
          <div class="form-group">
            <label class="form-label">
              Country
              <span class="required">*</span>
            </label>
            <select 
              v-model="billingInfo.country" 
              class="form-input form-select"
              :disabled="billingFormLoading"
            >
              <option value="">Select a country</option>
              <option v-for="country in countries" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </select>
          </div>

          <!-- Error message -->
          <div v-if="error" class="modal-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {{ error }}
          </div>

          <!-- Actions -->
          <div class="modal-actions">
            <button type="button" class="modal-btn secondary" @click="closeBillingForm" :disabled="billingFormLoading">
              Cancel
            </button>
            <button 
              type="submit"
              class="modal-btn primary" 
              :disabled="!isBillingFormValid() || billingFormLoading"
            >
              <span v-if="billingFormLoading" class="btn-content">
                <span class="btn-spinner"></span>
                Processing...
              </span>
              <span v-else class="btn-content">
                Continue to Payment
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Upgrade Preview Modal -->
    <div v-if="showUpgradeModal" class="modal-overlay" @click.self="closeUpgradeModal">
      <div class="modal-content upgrade-modal">
        <button class="modal-close" @click="closeUpgradeModal" :disabled="confirmingUpgrade">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div class="modal-header">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5 12 12 5 19 12"></polyline>
          </svg>
          <h2>Upgrade to {{ upgradePlan ? getPlanName(upgradePlan) : '' }}</h2>
        </div>

        <!-- Loading State -->
        <div v-if="upgradePreviewLoading" class="modal-loading">
          <div class="spinner"></div>
          <p>Loading upgrade details...</p>
        </div>

        <!-- Preview Content -->
        <div v-else-if="upgradePreview" class="upgrade-preview-content">
          <!-- Billing Summary -->
          <div class="billing-summary">
            <h3 class="summary-title">Billing Summary</h3>
            
            <div class="summary-row credit" v-if="upgradePreview.update_summary?.credit">
              <span class="summary-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Prorated Credit
              </span>
              <span class="summary-value credit-value">
                {{ formatCurrency(upgradePreview.update_summary.credit.amount, upgradePreview.update_summary.credit.currency_code) }}
              </span>
            </div>
            
            <div class="summary-row charge" v-if="upgradePreview.update_summary?.charge">
              <span class="summary-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                New Plan Charge
              </span>
              <span class="summary-value">
                {{ formatCurrency(upgradePreview.update_summary.charge.amount, upgradePreview.update_summary.charge.currency_code) }}
              </span>
            </div>
            
            <div class="summary-divider"></div>
            
            <div class="summary-row total" v-if="upgradePreview.update_summary?.result">
              <span class="summary-label">
                <strong>{{ upgradePreview.update_summary.result.action === 'charge' ? 'Amount Due Today' : 'Credit Applied' }}</strong>
              </span>
              <span class="summary-value total-value" :class="{ 'is-credit': upgradePreview.update_summary.result.action === 'credit' }">
                {{ formatCurrency(upgradePreview.update_summary.result.amount, upgradePreview.update_summary.result.currency_code) }}
              </span>
            </div>
          </div>

          <!-- Next Billing Info -->
          <div class="next-billing" v-if="upgradePreview.next_transaction?.billing_period">
            <div class="next-billing-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div class="next-billing-info">
              <span class="next-billing-label">Next billing date</span>
              <span class="next-billing-date">{{ formatDate(upgradePreview.next_transaction.billing_period.starts_at) }}</span>
            </div>
          </div>

          <!-- Terms Checkbox -->
          <label class="terms-checkbox">
            <input type="checkbox" v-model="agreedToTerms" :disabled="confirmingUpgrade">
            <span class="checkbox-custom"></span>
            <span class="terms-text">I agree to the <a href="/terms" target="_blank">Terms of Service</a></span>
          </label>

          <!-- Error message -->
          <div v-if="error" class="modal-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {{ error }}
          </div>

          <!-- Actions -->
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="closeUpgradeModal" :disabled="confirmingUpgrade">
              Cancel
            </button>
            <button 
              class="modal-btn primary upgrade-btn" 
              :disabled="!agreedToTerms || confirmingUpgrade"
              @click="confirmUpgrade"
            >
              <span v-if="confirmingUpgrade" class="btn-content">
                <span class="btn-spinner"></span>
                Processing...
              </span>
              <span v-else class="btn-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
                Upgrade
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api, getCurrentUser, clearCurrentUserCache } from '../utils/api'
import { countryCodes } from '../utils/countryCodes'

export default {
  name: 'UserSubscriptionPlansView',
  data() {
    return {
      plans: [],
      loading: true,
      error: null,
      userSubscriptionPlanId: null,
      currentSubscriptionPlan: null,
      upgradingPlanId: null,
      paddleInitialized: false,
      paddleInitializing: false,
      userData: null,
      managingSubscription: false,
      // Upgrade preview modal
      showUpgradeModal: false,
      upgradePreview: null,
      upgradePreviewLoading: false,
      upgradePlan: null,
      agreedToTerms: false,
      confirmingUpgrade: false,
      // Billing info form
      showBillingForm: false,
      billingFormPlan: null,
      billingFormLoading: false,
      billingFormNeedsDoubleCheck: false,
      billingInfo: {
        isBusiness: false,
        billingName: '',
        companyName: '',
        addressLine: '',
        city: '',
        postalCode: '',
        country: '',
        vatId: ''
      },
      countries: countryCodes
    }
  },
  computed: {
    userHasPaidSubscription() {
      // Check if user has a non-free subscription
      return this.currentSubscriptionPlan && !this.currentSubscriptionPlan.isFree
    },
    scheduledCancellation() {
      const changes = this.userData?.subscription?.scheduledChanges
      if (!Array.isArray(changes)) return null
      return changes.find(c => c.action === 'cancel') || null
    },
    hasScheduledCancellation() {
      return this.scheduledCancellation !== null
    },
    scheduledDowngrade() {
      const changes = this.userData?.subscription?.scheduledChanges
      if (!Array.isArray(changes)) return null
      return changes.find(c => c.action === 'downgrade') || null
    },
    hasScheduledDowngrade() {
      return this.scheduledDowngrade !== null
    },
    scheduledDowngradePlanName() {
      if (!this.scheduledDowngrade) return null
      const newPlanId = this.scheduledDowngrade.newPlanId
      const plan = this.plans.find(p => p.id === newPlanId)
      return plan ? this.getPlanName(plan) : null
    },
    monthlyPlans() {
      // Exclude free plan from selectable cards - free is only shown as Current Subscription when it's the user's plan
      const filtered = this.plans.filter(plan => !plan.isFree && plan.billingCycle === 'monthly')
      filtered.sort((a, b) => {
        const aSites = a.siteCount !== null ? a.siteCount : 0
        const bSites = b.siteCount !== null ? b.siteCount : 0
        return aSites - bSites
      })
      return filtered
    },
    yearlyPlans() {
      // Filter yearly plans only (exclude free plans as they're shown in monthly)
      const filtered = this.plans.filter(plan => 
        !plan.isFree && plan.billingCycle === 'yearly'
      )
      
      // Sort by siteCount ascending
      filtered.sort((a, b) => {
        const aSites = a.siteCount !== null ? a.siteCount : 0
        const bSites = b.siteCount !== null ? b.siteCount : 0
        return aSites - bSites
      })
      
      return filtered
    }
  },
  async created() {
    await this.fetchData()
    this.initializePaddle()
  },
  methods: {
    initializePaddle() {
      // Prevent multiple initializations
      if (this.paddleInitializing || this.paddleInitialized) {
        return
      }
      
      this.paddleInitializing = true
      
      // Wait for Paddle to be available (script might still be loading)
      const initPaddle = () => {
        if (typeof window.Paddle !== 'undefined') {
          // Get and clean token/environment values (trim any whitespace or comments)
          const paddleToken = (process.env.VUE_APP_PADDLE_CLIENT_TOKEN || '').trim()
          const paddleEnv = (process.env.VUE_APP_PADDLE_ENVIRONMENT || 'sandbox').split('#')[0].trim()
          
          console.log('Paddle token:', paddleToken ? `${paddleToken.substring(0, 15)}... (length: ${paddleToken.length})` : 'NOT SET')
          console.log('Paddle environment:', paddleEnv)
          console.log('Raw env values:', { 
            token: process.env.VUE_APP_PADDLE_CLIENT_TOKEN, 
            env: process.env.VUE_APP_PADDLE_ENVIRONMENT 
          })
          
          if (!paddleToken) {
            console.error('Paddle client token not configured. Set VUE_APP_PADDLE_CLIENT_TOKEN environment variable.')
            console.error('For sandbox, use a token prefixed with "test_" from your Paddle Dashboard.')
            this.paddleInitializing = false
            return
          }
          
          // Validate token format
          if (paddleEnv === 'sandbox' && !paddleToken.startsWith('test_')) {
            console.warn('Sandbox environment expects a token starting with "test_". Current token may be invalid.')
          }
          
          try {
            // Set environment first (must be done before Initialize)
            if (window.Paddle.Environment && typeof window.Paddle.Environment.set === 'function') {
              window.Paddle.Environment.set(paddleEnv)
              console.log('Paddle environment set to:', paddleEnv)
              // Give environment time to be applied
              setTimeout(() => {
                // Initialize Paddle after environment is set
                try {
                  window.Paddle.Initialize({
                    token: paddleToken,
                    eventCallback: (event) => {
                      console.log('Paddle event:', event)
                      if (event.name === 'checkout.completed') {
                        console.log('Paddle checkout completed!', event)
                        // Clear user cache and refresh subscription data
                        clearCurrentUserCache()
                        this.fetchData()
                      } else if (event.name === 'checkout.closed') {
                        console.log('Paddle checkout closed')
                        // Reset upgrading state when checkout is closed
                        this.upgradingPlanId = null
                      } else if (event.name === 'checkout.loaded') {
                        // Checkout is loaded and ready
                        console.log('Paddle checkout loaded')
                      }
                    }
                  })
                  
                  // Wait for Paddle to be fully ready (internal state needs time to initialize)
                  // Give Paddle more time to set up internal configuration
                  setTimeout(() => {
                    this.paddleInitialized = true
                    this.paddleInitializing = false
                    console.log('Paddle initialized successfully')
                  }, 2000) // Wait 2 seconds for internal state to be ready
                } catch (initError) {
                  console.error('Error during Paddle.Initialize:', initError)
                  this.paddleInitialized = false
                  this.paddleInitializing = false
                }
              }, 100) // Small delay after setting environment
            } else {
              console.error('Paddle.Environment.set is not available')
              this.paddleInitialized = false
              this.paddleInitializing = false
            }
          } catch (error) {
            console.error('Error initializing Paddle:', error)
            this.paddleInitialized = false
            this.paddleInitializing = false
          }
        } else {
          // Retry after a short delay if Paddle isn't loaded yet
          setTimeout(initPaddle, 100)
        }
      }
      
      initPaddle()
    },
    async fetchData() {
      try {
        this.loading = true
        this.error = null
        
        // Fetch user info first to get the subscription plan ID
        const userData = await getCurrentUser(this.$router)
        console.log('User data from /me:', userData)
        
        // Store user data for Paddle checkout
        this.userData = userData
        
        if (userData?.subscription?.subscriptionPlanId) {
          this.userSubscriptionPlanId = userData.subscription.subscriptionPlanId
          console.log('User subscription plan ID:', this.userSubscriptionPlanId)
        } else {
          console.log('No subscription plan ID found in user data')
        }
        
        // Then fetch plans
        const plansData = await api('/subscription-plans', {}, this.$router)
        this.plans = Array.isArray(plansData) ? plansData : []
        console.log('Available plan IDs:', this.plans.map(p => ({ id: p.id, name: p.name })))
        
        // Find the current subscription plan (to match by name across billing cycles)
        if (this.userSubscriptionPlanId) {
          this.currentSubscriptionPlan = this.plans.find(p => p.id === this.userSubscriptionPlanId)
          console.log('Current subscription plan found:', this.currentSubscriptionPlan ? this.currentSubscriptionPlan.name : 'NONE')
        }
      } catch (error) {
        this.error = error.code || 'Failed to load subscription plans. Please try again.'
        console.error('Error fetching data:', error)
      } finally {
        this.loading = false
      }
    },
    isCurrentPlan(plan) {
      // Only exact ID match - different billing cycles are different plans
      return this.userSubscriptionPlanId && plan.id === this.userSubscriptionPlanId
    },
    async selectPlan(plan) {
      if (!plan.isActive || plan.isFree || this.isCurrentPlan(plan)) {
        return
      }
      // Downgrade is not offered: user must cancel subscription first to change to a smaller plan
      if (this.isDowngrade(plan)) {
        return
      }
      
      // If user has a paid subscription and this is an upgrade, show preview modal
      if (this.userHasPaidSubscription && this.isUpgrade(plan)) {
        await this.showUpgradePreview(plan)
        return
      }
      
      // Check if user already has a complete billing profile (including postal code)
      if (this.userData?.billingProfile?.billingName && this.userData?.billingProfile?.postalCode) {
        // User already has complete billing profile, proceed directly to checkout
        await this.proceedToCheckout(plan)
        return
      }
      
      // Show billing form before checkout
      this.showBillingFormModal(plan)
    },
    showBillingFormModal(plan) {
      this.billingFormPlan = plan
      this.showBillingForm = true
      this.error = null
      this.billingFormNeedsDoubleCheck = false
      // Reset form
      this.billingInfo = {
        isBusiness: false,
        billingName: '',
        companyName: '',
        addressLine: '',
        city: '',
        postalCode: '',
        country: '',
        vatId: ''
      }
    },
    closeBillingForm() {
      this.showBillingForm = false
      this.billingFormPlan = null
      this.error = null
      this.billingFormNeedsDoubleCheck = false
    },
    isBillingFormValid() {
      // Required fields for personal: billingName, country, postalCode
      if (!this.billingInfo.billingName.trim() || !this.billingInfo.country || !this.billingInfo.postalCode.trim()) {
        return false
      }
      // Additional required fields for business
      if (this.billingInfo.isBusiness) {
        if (!this.billingInfo.companyName.trim() || 
            !this.billingInfo.addressLine.trim() || 
            !this.billingInfo.city.trim()) {
          return false
        }
      }
      return true
    },
    async submitBillingInfo() {
      if (!this.isBillingFormValid() || !this.billingFormPlan) return
      
      this.billingFormLoading = true
      this.error = null
      
      try {
        // Build payload, add doubleCheck if retrying after a 400 error
        const payload = { ...this.billingInfo }
        if (this.billingFormNeedsDoubleCheck) {
          payload.doubleCheck = true
        }
        
        // Save billing info
        await api('/users/me/billing-profile', {
          method: 'PUT',
          body: JSON.stringify(payload)
        }, this.$router)
        
        // Close billing form and proceed to checkout
        const plan = this.billingFormPlan
        this.closeBillingForm()
        await this.proceedToCheckout(plan)
      } catch (error) {
        // Check if it's a 400 error
        if (error.status === 400) {
          this.error = error.code || 'Please verify your billing information and try again.'
          this.billingFormNeedsDoubleCheck = true
        } else {
          this.error = error.code || 'Failed to save billing information. Please try again.'
        }
        console.error('Error saving billing info:', error)
      } finally {
        this.billingFormLoading = false
      }
    },
    async showUpgradePreview(plan) {
      this.upgradePlan = plan
      this.upgradePreviewLoading = true
      this.showUpgradeModal = true
      this.agreedToTerms = false
      this.error = null
      
      try {
        const response = await api(`/subscriptions/upgrade/preview?newPlanId=${plan.id}&upgrade=true`, {}, this.$router)
        this.upgradePreview = response
      } catch (error) {
        this.error = error.code || 'Failed to load upgrade preview. Please try again.'
        console.error('Error fetching upgrade preview:', error)
        this.showUpgradeModal = false
      } finally {
        this.upgradePreviewLoading = false
      }
    },
    closeUpgradeModal() {
      this.showUpgradeModal = false
      this.upgradePreview = null
      this.upgradePlan = null
      this.agreedToTerms = false
    },
    async confirmUpgrade() {
      if (!this.agreedToTerms || !this.upgradePlan) return
      
      this.confirmingUpgrade = true
      this.error = null
      
      try {
        await api('/subscriptions/change', {
          method: 'POST',
          body: JSON.stringify({ newPlanId: this.upgradePlan.id, upgrade: true })
        }, this.$router)
        
        // Success - close modal
        this.closeUpgradeModal()
        clearCurrentUserCache()
        
        // Wait 200ms then refresh user data and update current plan
        await new Promise(resolve => setTimeout(resolve, 200))
        const userData = await getCurrentUser(this.$router)
        this.userData = userData
        
        if (userData?.subscription?.subscriptionPlanId) {
          this.userSubscriptionPlanId = userData.subscription.subscriptionPlanId
          // Update current subscription plan
          this.currentSubscriptionPlan = this.plans.find(p => p.id === this.userSubscriptionPlanId)
        }
        
        // Refresh all data
        await this.fetchData()
      } catch (error) {
        this.error = error.code || 'Failed to upgrade subscription. Please try again.'
        console.error('Error upgrading subscription:', error)
      } finally {
        this.confirmingUpgrade = false
      }
    },
    formatCurrency(amount, currencyCode) {
      // Amount comes as string without decimal, convert to number with decimals
      const numericAmount = parseFloat(amount) / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'EUR'
      }).format(numericAmount)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatNextBillingDate() {
      if (!this.userData?.subscription?.nextBillingDate) return 'N/A'
      return this.formatDate(this.userData.subscription.nextBillingDate)
    },
    getCurrentPlanName() {
      if (!this.currentSubscriptionPlan) {
        return 'No active plan'
      }
      return this.getPlanName(this.currentSubscriptionPlan)
    },
    async proceedToCheckout(plan) {
      this.upgradingPlanId = plan.id
      this.error = null
      
      try {
        const payload = {
          priceId: plan.paddlePriceId
        }
        
        const response = await api('/billing/checkout', {
          method: 'POST',
          body: JSON.stringify(payload)
        }, this.$router)
        
        // Check if we have a checkoutId from the response
        if (response && response.checkoutId) {
          // Wait for Paddle to be fully initialized before opening checkout
          const openCheckout = (retryCount = 0) => {
            const maxRetries = 50 // 5 seconds max wait (50 * 100ms)
            
            // Check if Paddle is available and Checkout.open is a function
            const isPaddleReady = typeof window.Paddle !== 'undefined' && 
                                 window.Paddle.Checkout && 
                                 typeof window.Paddle.Checkout.open === 'function' &&
                                 this.paddleInitialized
            
            if (isPaddleReady) {
              try {
                // Build checkout options with transaction ID and customer info
                const checkoutOptions = {
                  settings: {
                    variant: 'one-page'
                  },
                  transactionId: response.checkoutId
                }
                
                // Add customer info if available
                if (this.userData) {
                  checkoutOptions.customer = {
                    email: this.userData.email || undefined
                  }
                  
                  // Add address if available
                  if (this.userData.countryCode) {
                    checkoutOptions.customer.address = {
                      countryCode: this.userData.countryCode,
                      postalCode: this.userData.postalCode || undefined
                    }
                  }
                }
                
                window.Paddle.Checkout.open(checkoutOptions)
                console.log('Paddle checkout opened with transaction ID:', response.checkoutId, 'Customer:', checkoutOptions.customer)
              } catch (error) {
                console.error('Error opening Paddle checkout:', error)
                // If it's the checkoutFrontEndBase error, retry after a longer delay
                const errorStr = error.message || error.toString() || ''
                if (errorStr.includes('checkoutFrontEndBase') && retryCount < 15) {
                  console.log(`Retrying checkout open (attempt ${retryCount + 1}/15) after initialization delay...`)
                  setTimeout(() => openCheckout(retryCount + 1), 1000) // Wait 1 second between retries
                  return
                }
                this.error = 'Failed to open checkout. Please try again.'
                this.upgradingPlanId = null
              }
            } else if (retryCount < maxRetries) {
              // Retry after a short delay if Paddle isn't ready yet
              setTimeout(() => openCheckout(retryCount + 1), 100)
            } else {
              // Max retries reached, show error
              console.error('Paddle checkout failed to open: Paddle not initialized after max retries')
              this.error = 'Checkout is not ready. Please try again in a moment.'
              this.upgradingPlanId = null
            }
          }
          
          openCheckout()
          // Note: Don't reset upgradingPlanId here - let the checkout.completed or checkout.closed event handle it
        } else {
          // Fallback: if no checkoutId, refresh data (legacy behavior)
          clearCurrentUserCache()
          await this.fetchData()
          this.upgradingPlanId = null
          console.log('Checkout initiated successfully (no Paddle checkout)')
        }
      } catch (error) {
        this.error = error.code || 'Failed to initiate checkout. Please try again.'
        console.error('Error initiating checkout:', error)
        this.upgradingPlanId = null
      }
    },
    formatDiscount(discount) {
      if (!discount || !discount.isActive) return ''
      
      if (discount.discountType === 'percentage') {
        return `${discount.discountValue}% OFF`
      } else {
        return `€${discount.discountValue} OFF`
      }
    },
    formatInodes(inodes) {
      if (inodes === null || inodes === undefined) return 'Unlimited'
      return inodes.toLocaleString('en-US')
    },
    formatExtraScans(scans) {
      if (scans === null || scans === undefined) return 'N/A'
      if (scans === 0) return 'None'
      return scans.toString()
    },
    getAIStatus(plan) {
      return plan.isFree ? 'Enabled' : 'Enabled'
    },
    getPlanName(plan) {
      // Remove "Yearly" suffix if present
      if (plan.name.endsWith(' Yearly')) {
        return plan.name.replace(' Yearly', '')
      }
      return plan.name
    },
    isPlanDisabled(plan) {
      // Downgrades are not offered: user must cancel first to change to a smaller plan
      if (this.isDowngrade(plan)) {
        return true
      }
      // Disable upgrade/downgrade if there's a scheduled cancellation
      if (this.hasScheduledCancellation && !this.isCurrentPlan(plan)) {
        return true
      }
      // Disable downgrade options if there's already a scheduled downgrade
      if (this.hasScheduledDowngrade && this.isDowngrade(plan)) {
        return true
      }
      // Disable yearly to monthly downgrades - user must cancel and recreate
      if (this.isYearlyToMonthlyChange(plan)) {
        return true
      }
      // Disable monthly to yearly downgrade if yearly plan has fewer sites
      if (this.isMonthlyToYearlyDowngrade(plan)) {
        return true
      }
      return false
    },
    isYearlyToMonthlyChange(plan) {
      // Check if this would be switching from yearly to monthly billing
      // This is not allowed - user must cancel and create new subscription
      if (!this.currentSubscriptionPlan) {
        return false
      }
      const currentCycle = this.currentSubscriptionPlan.billingCycle
      const planCycle = plan.billingCycle
      return currentCycle === 'yearly' && planCycle === 'monthly'
    },
    isMonthlyToYearlyDowngrade(plan) {
      // Disable downgrade from monthly to yearly if yearly plan has fewer sites
      if (!this.currentSubscriptionPlan) {
        return false
      }
      const currentCycle = this.currentSubscriptionPlan.billingCycle
      const planCycle = plan.billingCycle
      const currentSites = this.currentSubscriptionPlan.siteCount ?? 0
      const planSites = plan.siteCount ?? 0
      return currentCycle === 'monthly' && planCycle === 'yearly' && planSites < currentSites
    },
    formatCancellationDate() {
      if (!this.scheduledCancellation) return ''
      const date = new Date(this.scheduledCancellation.effectiveAt)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatScheduledChangeDate() {
      if (!this.scheduledDowngrade?.effectiveAt) return ''
      const date = new Date(this.scheduledDowngrade.effectiveAt)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    isMostPopular(plan) {
      // Show "Most Popular" ribbon for Developer plans
      const planName = plan.name.toLowerCase()
      return planName.includes('developer')
    },
    isUpgrade(plan) {
      // Check if plan is an upgrade from current subscription
      // Upgrade if: more sites, OR same sites but switching from monthly to yearly
      if (!this.currentSubscriptionPlan) {
        return false // No current plan, so it's not an upgrade
      }
      const currentSites = this.currentSubscriptionPlan.siteCount ?? 0
      const planSites = plan.siteCount ?? 0
      const currentCycle = this.currentSubscriptionPlan.billingCycle
      const planCycle = plan.billingCycle
      
      // More sites = upgrade
      if (planSites > currentSites) {
        return true
      }
      // Same sites but monthly → yearly = upgrade
      if (planSites === currentSites && currentCycle === 'monthly' && planCycle === 'yearly') {
        return true
      }
      return false
    },
    isDowngrade(plan) {
      // Check if plan is a downgrade from current subscription
      // Downgrade if: fewer sites, OR same sites but switching from yearly to monthly
      if (!this.currentSubscriptionPlan) {
        return false // No current plan, so it's not a downgrade
      }
      const currentSites = this.currentSubscriptionPlan.siteCount ?? 0
      const planSites = plan.siteCount ?? 0
      const currentCycle = this.currentSubscriptionPlan.billingCycle
      const planCycle = plan.billingCycle
      
      // Fewer sites = downgrade
      if (planSites < currentSites) {
        return true
      }
      // Same sites but yearly → monthly = downgrade
      if (planSites === currentSites && currentCycle === 'yearly' && planCycle === 'monthly') {
        return true
      }
      return false
    },
    async manageSubscription() {
      this.managingSubscription = true
      this.error = null
      
      try {
        const response = await api('/billing/customer-portal', {}, this.$router)
        
        // Open the portal link in a new tab
        if (response && response.link) {
          window.open(response.link, '_blank')
        } else {
          console.error('No portal link returned from API')
          this.error = 'Failed to open subscription management. Please try again.'
        }
      } catch (error) {
        this.error = error.code || 'Failed to open subscription management. Please try again.'
        console.error('Error opening customer portal:', error)
      } finally {
        this.managingSubscription = false
      }
    }
  }
}
</script>

<style scoped>
.subscription-plans-view {
  position: relative;
  min-height: 100vh;
  padding: 40px 20px;
  overflow: hidden;
}

/* Background elements */
.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  pointer-events: none;
  animation: pulse-glow 8s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
}

.content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 48px;
  gap: 24px;
  animation: fade-in 0.6s ease-out;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.header-content {
  text-align: center;
}

.page-title {
  color: var(--text-primary);
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.page-subtitle {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

/* Current Subscription Info */
.current-subscription-info {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  animation: fade-in 0.4s ease-out;
}

.subscription-info-header {
  margin-bottom: 16px;
}

.subscription-info-title {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.subscription-info-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.info-value {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  text-transform: capitalize;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge.cancelled,
.status-badge.past_due {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.status-badge.paused {
  background: rgba(251, 191, 36, 0.15);
  color: #f59e0b;
}

/* Cancellation Notification */
.cancellation-notification {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  animation: fade-in 0.4s ease-out;
}

.cancellation-notification svg {
  color: #ef4444;
  flex-shrink: 0;
}

/* Downgrade Notification */
.downgrade-notification {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  animation: fade-in 0.4s ease-out;
}

.downgrade-notification svg {
  color: #f59e0b;
  flex-shrink: 0;
}

.downgrade-notification .notification-content strong {
  color: #f59e0b;
}

.notification-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.notification-content strong {
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
}

.notification-content span {
  color: var(--text-secondary);
  font-size: 13px;
}

.notification-link {
  color: #ef4444;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.notification-link:hover {
  color: #dc2626;
}

.billing-toggle {
  display: flex;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
}

.toggle-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-option:hover {
  color: var(--text-primary);
  background: rgba(59, 130, 246, 0.1);
}

.toggle-option.active {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.toggle-option svg {
  opacity: 0.7;
}

.toggle-option.active svg {
  opacity: 1;
}

.save-badge {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.toggle-option.active .save-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Loading, Error, Empty states */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
  animation: fade-in 0.4s ease-out;
}

.loading-state p,
.error-state p,
.empty-state p {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

.error-state svg,
.empty-state svg {
  color: var(--text-muted);
}

.error-state svg {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.retry-btn {
  padding: 10px 24px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

/* Plans Container */
.plans-container {
  display: flex;
  flex-direction: column;
  gap: 48px;
  animation: slide-up 0.6s ease-out;
}

/* Plans Section */
.plans-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.section-title svg {
  color: var(--accent-color);
}

.section-title .save-badge {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
}

/* Plans Grid */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
}

@keyframes slide-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Plan Card */
.plan-card {
  position: relative;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.plan-card.popular {
  border-color: rgba(255, 255, 255, 0.15);
}

.plan-card.current {
  border-color: rgba(34, 197, 94, 0.4);
}

.plan-card.free-plan {
  cursor: default;
}

.plan-card.free-plan:hover {
  transform: none;
}

/* Badges */
.popular-badge,
.discount-badge,
.current-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 10;
}

.popular-badge {
  background: rgba(59, 130, 246);
  color: #FFFFFF;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.discount-badge {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  top: -12px;
  left: auto;
  right: 20px;
  transform: none;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.current-badge {
  background: rgba(34, 197, 94);
  color: #FFFFFF;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

/* Plan Header */
.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-top: 4px;
}

.plan-name {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.free-badge {
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* Plan Price */
.plan-price {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.price-currency {
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 600;
  margin-top: 4px;
}

.price-amount {
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.price-cents {
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 600;
  margin-top: 4px;
}

.price-meta {
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  margin-top: 8px;
}

.original-price {
  color: var(--text-muted);
  font-size: 14px;
  text-decoration: line-through;
}

.price-period {
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

/* Divider */
.plan-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  margin-bottom: 12px;
}

/* Plan Features */
.plan-features {
  flex: 1;
  margin-bottom: 12px;
}

.feature {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.feature:last-child {
  border-bottom: none;
}

.feature svg {
  color: var(--accent-color);
  flex-shrink: 0;
}

.feature-label {
  color: var(--text-secondary);
  font-size: 14px;
  flex: 1;
}

.feature-value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.feature-value.ai-enabled {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #22c55e;
}

.feature-value.ai-enabled svg {
  color: #22c55e;
}

.feature.description {
  flex-direction: column;
  align-items: flex-start;
  padding-top: 12px;
}

.description-text {
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

/* Plan Footer */
.plan-footer {
  margin-top: auto;
}

.plan-footer-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.select-button {
  flex: 1;
  min-width: 0;
  padding: 8px 14px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.select-button:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

.select-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-muted);
  border-color: rgba(255, 255, 255, 0.1);
}


.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Manage subscription button */
.manage-subscription-button {
  flex: 1;
  min-width: 0;
  padding: 8px 14px;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.manage-subscription-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: var(--text-primary);
}

.manage-subscription-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.manage-subscription-button .btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.manage-subscription-button .btn-spinner {
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--text-secondary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fade-in 0.2s ease-out;
  padding: 20px;
}

.modal-content {
  position: relative;
  background: rgba(30, 41, 59, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slide-up 0.3s ease-out;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.modal-close:hover:not(:disabled) {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.1);
}

.modal-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.modal-header svg {
  color: var(--accent-color);
}

.modal-header h2 {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  gap: 16px;
}

.modal-loading p {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
}

/* Billing Summary */
.billing-summary {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-title {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 14px;
}

.summary-label svg {
  color: var(--text-muted);
}

.summary-value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.summary-value.credit-value {
  color: #22c55e;
}

.summary-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 8px 0;
}

.summary-row.total {
  padding-top: 12px;
}

.summary-row.total .summary-label {
  color: var(--text-primary);
}

.total-value {
  font-size: 18px;
  color: var(--accent-color);
}

.total-value.is-credit {
  color: #22c55e;
}

/* Next Billing */
.next-billing {
  display: flex;
  align-items: center;
  gap: 14px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.next-billing-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 10px;
}

.next-billing-icon svg {
  color: var(--accent-color);
}

.next-billing-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.next-billing-label {
  color: var(--text-secondary);
  font-size: 12px;
}

.next-billing-date {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
}

/* Terms Checkbox */
.terms-checkbox {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 20px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.terms-checkbox:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.terms-checkbox input[type="checkbox"] {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.terms-checkbox input[type="checkbox"]:checked + .checkbox-custom {
  background: rgba(59, 130, 246, 0.2);
  border-color: var(--accent-color);
}

.terms-checkbox input[type="checkbox"]:checked + .checkbox-custom::after {
  content: '';
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.terms-text {
  color: var(--text-secondary);
  font-size: 14px;
}

.terms-text a {
  color: var(--accent-color);
  text-decoration: none;
}

.terms-text a:hover {
  text-decoration: underline;
}

/* Modal Error */
.modal-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 20px;
  color: #ef4444;
  font-size: 14px;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
}

.modal-btn.secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--text-secondary);
}

.modal-btn.secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: var(--text-primary);
}

.modal-btn.secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: 1px solid #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.1);
  font-weight: 600;
}

.modal-btn.primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border-color: #2563eb;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2);
  transform: translateY(-2px);
}

.modal-btn.primary:disabled {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--text-muted);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  opacity: 0.6;
}

.modal-btn .btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Billing Form Modal */
.billing-form-modal {
  max-width: 520px;
}

.billing-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.business-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 14px 16px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.business-toggle:hover {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.3);
}

.business-toggle input[type="checkbox"] {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.business-toggle input[type="checkbox"]:checked + .toggle-switch {
  background: rgba(59, 130, 246, 0.3);
  border: 1px solid var(--accent-color);
}

.business-toggle input[type="checkbox"]:checked + .toggle-switch::after {
  left: 23px;
}

.toggle-label {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-label {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.form-label .required {
  color: #ef4444;
  margin-left: 2px;
}

.form-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 14px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  background: rgba(0, 0, 0, 0.3);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
  cursor: pointer;
}

.form-select option {
  background: #1e293b;
  color: var(--text-primary);
}

/* Responsive */

/* iPad Mini and similar tablets - 2 columns */
@media (min-width: 768px) and (max-width: 1100px) {
  .plans-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .plan-card {
    padding: 18px;
  }
  
  .price-amount {
    font-size: 32px;
  }
  
  .plan-name {
    font-size: 16px;
  }
  
  .section-title {
    font-size: 20px;
  }
}

@media (max-width: 767px) {
  .subscription-plans-view {
    padding: 24px 16px;
  }
  
  .page-title {
    font-size: 28px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .cancellation-notification,
  .downgrade-notification {
    padding: 12px 16px;
    margin-bottom: 24px;
  }
  
  .plans-container {
    gap: 32px;
  }
  
  .plans-section {
    gap: 16px;
  }
  
  .section-title {
    font-size: 20px;
  }
  
  .plans-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .plan-card {
    padding: 18px;
  }
  
  .price-amount {
    font-size: 32px;
  }
  
  .plan-name {
    font-size: 16px;
  }
}
</style>

